import React from 'react';

import {
  ActionIcon,
  AutocompleteItem,
  Checkbox,
  Divider,
  Group,
  Input,
  NumberInput,
  Paper,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { AxiosError, AxiosResponse } from 'axios';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import ClassCoachRoleAutoComplete, {
  ClassCoachRoleAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/ClassCoachRoleAutoComplete';
import ClassTypeAutoComplete, {
  ClassTypeAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/ClassTypeAutoComplete';
import LocationAutoComplete, {
  LocationAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/LocationAutoComplete';
import StaffAutoComplete, {
  StaffAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/StaffAutoComplete';
import MoneyField from 'components/MoneyField';
import { useLocalization } from 'context/localization-context';
import dayjs, { Dayjs } from 'dayjs';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { UseMutationResult } from 'react-query';
import { RRuleSet, rrulestr } from 'rrule';
import { DayAbbreviation, ErrorMessage, KiloClass, KiloClassRetrieve, KiloClassWrite } from 'types';
import { TimeUnitEnum } from 'types/enums';
import { getChangedKeysForClassEdit } from 'utils/analytics-selectors';
import { getReservationAndCancellationSettings } from 'utils/event-json-settings.helpers';
import {
  calculateEventEndDateTime,
  createRrule,
  getDayAbbreviationFromDate,
  getDayAbbreviationsFromRruleBydays,
  getDayDiff,
  parseRruleForFrequency,
  parseRruleForMonthDay,
  type RecurrenceFrequency,
} from 'utils/event.helpers';
import { setMaxLateCancellationIntervalCount } from 'utils/functions';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  kiloClassId: string;
  startDate: Date;
  startTime: string;
  duration: number;
  endDate: Date | null;
  classCoachClassCoachRoles: {
    kiloClassCoachKiloClassCoachRoleId: string;
    coach: StaffAutoCompleteValue;
    classCoachRole: ClassCoachRoleAutoCompleteValue;
  }[];
  capacity: number;
  kiloClassType: ClassTypeAutoCompleteValue;
  location: LocationAutoCompleteValue;
  rootKiloClass: string | null;
  days: DayAbbreviation[];
  frequency: RecurrenceFrequency;
  waitlistLimit: number;
  useLateCancellationFee: boolean;
  useNoShowFee: boolean;
  reservationAndCancellationSettings: {
    reservationWindowOpenInterval: 'minute' | 'hour' | 'day';
    reservationWindowOpenIntervalCount: number;
    reservationWindowCloseInterval: 'minute' | 'hour' | 'day';
    reservationWindowCloseIntervalCount: number;
    allowLateCancellation: boolean;
    cancellationInterval: 'minute' | 'hour' | 'day';
    cancellationIntervalCount: number;
    cancellationFee: number;
    cancellationCreditDeduction: boolean;
    noShowFee: number;
    noShowCreditDeduction: boolean;
    waitlistMax: number;
  };
  classEnd: Dayjs | null;
  dayDiff: string;
}

interface IDetailsEditAllFutureClassesFormProps {
  kiloClass: KiloClassRetrieve;
  closeHandler: () => void;
  formId: string;
  updateMutation: UseMutationResult<AxiosResponse<KiloClass>, AxiosError<ErrorMessage>, KiloClassWrite>;
  prefillStartTime?: string;
  prefillOriginalCoachId?: string;
  prefillNewCoachId?: string;
  prefillNewCoachName?: string;
}

const timeUnitOptions = [
  { value: TimeUnitEnum.MINUTE, label: 'Min(s)' },
  { value: TimeUnitEnum.HOUR, label: 'Hour(s)' },
  { value: TimeUnitEnum.DAY, label: 'Day(s)' },
];

const DetailsEditAllFutureClassesForm = ({
  kiloClass,
  closeHandler,
  formId,
  updateMutation,
  prefillStartTime,
  prefillOriginalCoachId,
  prefillNewCoachId,
  prefillNewCoachName,
}: IDetailsEditAllFutureClassesFormProps): JSX.Element => {
  const theme = useMantineTheme();
  const { country } = useLocalization();
  const staffTracking = useStaffTracking();

  const [settingsOpened, { toggle: handleSettingsToggle }] = useDisclosure();

  const effectiveStartTime = prefillStartTime ?? kiloClass.startTime;

  const initialDays = kiloClass.rootKiloClass?.rrule
    ? getDayAbbreviationsFromRruleBydays(
        (rrulestr(kiloClass.rootKiloClass.rrule, { forceset: true }) as RRuleSet)._rrule[0].options
          .byweekday,
      )
    : [getDayAbbreviationFromDate(dayjs(kiloClass.startDate).toDate())];

  // Preserve the series' original monthly anchor day. `startDate` here is the clicked
  // occurrence and is rendered disabled, so on a clamped occurrence (Feb 28 of a 31st
  // series) it would otherwise re-anchor the whole series to the 28th on save.
  const initialMonthDay = parseRruleForMonthDay(kiloClass.rootKiloClass?.rrule);

  const initialClassEnd = calculateEventEndDateTime(
    kiloClass.duration,
    effectiveStartTime,
    dayjs(kiloClass.startDate).toDate(),
  );

  const reservationAndCancellationSettings = getReservationAndCancellationSettings(
    kiloClass.reservationAndCancellationSettings,
  );

  const newCoachAlreadyOnEvent =
    prefillNewCoachId != null &&
    kiloClass.kiloClassCoachKiloClassCoachRoles.some(r => r.coach.staffId === prefillNewCoachId);

  const initialFormValues: FormValues = {
    ...kiloClass,
    startDate: dayjs(kiloClass.startDate).toDate(),
    endDate: kiloClass.rootKiloClass?.endDate ? dayjs(kiloClass.rootKiloClass.endDate).toDate() : null,
    startTime: dayjs(`${kiloClass.startDate} ${effectiveStartTime}`).format('HH:mm').toString(),
    kiloClassType: {
      kiloClassTypeId: kiloClass.kiloClassType?.kiloClassTypeId || '',
      value: kiloClass.kiloClassType?.kiloClassTypeId || '',
      label: kiloClass.kiloClassType?.name || '',
    },
    classCoachClassCoachRoles: kiloClass.kiloClassCoachKiloClassCoachRoles
      .filter(ccccr => !(ccccr.coach.staffId === prefillOriginalCoachId && newCoachAlreadyOnEvent))
      .map(ccccr => {
        const isSwapped =
          prefillOriginalCoachId != null &&
          prefillNewCoachId != null &&
          ccccr.coach.staffId === prefillOriginalCoachId;
        const coachId = isSwapped ? prefillNewCoachId : ccccr.coach.staffId;
        const coachName = isSwapped ? prefillNewCoachName ?? '' : ccccr.coach.fullName;
        return {
          kiloClassCoachKiloClassCoachRoleId: ccccr.kiloClassCoachKiloClassCoachRoleId,
          coach: {
            staffId: coachId,
            value: coachId,
            label: coachName,
          },
          classCoachRole: {
            kiloClassCoachRoleId: ccccr.kiloClassCoachRole.kiloClassCoachRoleId,
            value: ccccr.kiloClassCoachRole.kiloClassCoachRoleId,
            label: ccccr.kiloClassCoachRole.name,
          },
        };
      }),
    location: {
      locationId: kiloClass.location?.locationId || '',
      label: kiloClass.location?.name || '',
      value: kiloClass.location?.locationId || '',
    },
    rootKiloClass: null,
    days: initialDays,
    frequency: parseRruleForFrequency(kiloClass.rootKiloClass?.rrule),
    waitlistLimit: kiloClass.waitlistLimit || 0,
    useLateCancellationFee: kiloClass.reservationAndCancellationSettings.cancellationFee !== 0,
    useNoShowFee: kiloClass.reservationAndCancellationSettings.noShowFee !== 0,
    reservationAndCancellationSettings: reservationAndCancellationSettings,
    classEnd: initialClassEnd,
    dayDiff: getDayDiff(dayjs(kiloClass.startDate), initialClassEnd),
  };

  const form = useForm<FormValues>({
    initialValues: initialFormValues,

    validate: {
      kiloClassType: value => (value ? null : 'Class type is required'),
      capacity: value => (value < 1 ? 'Capacity must be more or equal 1' : null),
      duration: value => (value < 15 ? 'Duration must be more or equal 15 mins' : null),
      startDate: value => (value ? null : 'Start date is required'),
      startTime: value => (value ? null : 'Start time is required'),
    },
  });

  const handleCloseAndFormReset = (): void => {
    closeHandler();
    form.reset();
  };

  const handleAddCoach = (): void => {
    form.insertListItem('classCoachClassCoachRoles', {
      kiloClassCoachKiloClassCoachRoleId: uuidv4(),
      coach: {
        staffId: '',
        value: '',
        label: '',
      },
      classCoachRole: {
        kiloClassCoachRoleId: '',
        value: '',
        label: '',
      },
    });
  };

  const handleCoachChange = (item: AutocompleteItem | null, index: number): void => {
    const selectedCoach = {
      staffId: item ? item.staffId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue(`classCoachClassCoachRoles.${index}.coach`, selectedCoach);
  };

  const handleClassCoachRoleChange = (item: AutocompleteItem | null, index: number): void => {
    const selectedClassCoachRole = {
      kiloClassCoachRoleId: item ? item.kiloClassCoachRoleId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue(`classCoachClassCoachRoles.${index}.classCoachRole`, selectedClassCoachRole);
  };

  const handleLocationChange = (item: LocationAutoCompleteValue | null): void => {
    const selectedLocation = {
      locationId: item ? item.locationId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('location', selectedLocation);
  };

  const handleClassTypeChange = (item: ClassTypeAutoCompleteValue | null): void => {
    const selectedKiloClassType = {
      kiloClassTypeId: item ? item.kiloClassTypeId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('kiloClassType', selectedKiloClassType);
  };

  const handleFrequencyChange = (value: string): void => {
    const frequency = value as RecurrenceFrequency;
    form.setFieldValue('frequency', frequency);
    // Weekly/biweekly need at least one weekday; seed the start date's weekday when
    // switching away from monthly (which carries no weekdays) so the rrule is valid.
    if (frequency !== 'monthly' && form.values.days.length === 0) {
      form.setFieldValue('days', [getDayAbbreviationFromDate(dayjs(form.values.startDate).toDate())]);
    }
  };

  const handleUpdate = (values: FormValues): void => {
    if (updateMutation.isLoading) return;
    const { errorNotification, successNotification } = notificationHandler();
    let rrule = createRrule(
      form.values.startDate,
      form.values.endDate,
      form.values.days,
      form.values.frequency,
      initialMonthDay,
    );

    const payload: KiloClassWrite = {
      ...values,
      kiloClassId: kiloClass?.rootKiloClass?.kiloClassId || '',
      kiloClassType: values.kiloClassType.kiloClassTypeId,
      kiloClassCoachKiloClassCoachRoles: values.classCoachClassCoachRoles.map(ccccr => ({
        kiloClassCoachRole: ccccr.classCoachRole.kiloClassCoachRoleId,
        coach: ccccr.coach.staffId,
      })),
      location: values.location?.locationId || null,
      startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
      endDate: values.endDate ? dayjs(values.endDate).format('YYYY-MM-DD') : null,
      rrule,
      waitlistLimit: values.waitlistLimit,
      reservationAndCancellationSettings: {
        reservationWindowOpenInterval:
          values.reservationAndCancellationSettings.reservationWindowOpenInterval,
        reservationWindowOpenIntervalCount:
          values.reservationAndCancellationSettings.reservationWindowOpenIntervalCount,
        reservationWindowCloseInterval:
          values.reservationAndCancellationSettings.reservationWindowCloseInterval,
        reservationWindowCloseIntervalCount:
          values.reservationAndCancellationSettings.reservationWindowCloseIntervalCount,
        allowLateCancellation: values.reservationAndCancellationSettings.allowLateCancellation,
        cancellationInterval: values.reservationAndCancellationSettings.cancellationInterval,
        cancellationIntervalCount: values.reservationAndCancellationSettings.cancellationIntervalCount,
        cancellationFee:
          values.useLateCancellationFee && values.reservationAndCancellationSettings.allowLateCancellation
            ? values.reservationAndCancellationSettings.cancellationFee
            : 0,
        cancellationCreditDeduction: values.reservationAndCancellationSettings.allowLateCancellation
          ? values.reservationAndCancellationSettings.cancellationCreditDeduction
          : false,
        noShowFee: values.useNoShowFee ? values.reservationAndCancellationSettings.noShowFee : 0,
        noShowCreditDeduction: values.reservationAndCancellationSettings.noShowCreditDeduction,
        waitlistMax: values.reservationAndCancellationSettings.waitlistMax,
      },
      editThisEventOnly: false,
      sendNotification: true,
    };

    const changedKeys: string[] = getChangedKeysForClassEdit(initialFormValues, values);

    updateMutation.mutate(payload, {
      onSuccess: () => {
        successNotification(`${values.kiloClassType.label} created successfully`);
        if (changedKeys.length > 0) {
          staffTracking.trackEventUpdate(
            kiloClass?.rootKiloClass?.kiloClassId || kiloClass.kiloClassId,
            values.kiloClassType.kiloClassTypeId,
            values.kiloClassType.label,
            'class',
            'all future events',
            changedKeys,
          );
        }
        handleCloseAndFormReset();
      },
      onError: error => {
        errorNotification('Updating classes failed', error.response?.data.message);
      },
    });
  };

  useShallowEffect(() => {
    if (form.values.duration && form.values.startTime) {
      const classEnd = calculateEventEndDateTime(
        form.values.duration,
        form.values.startTime,
        dayjs(form.values.startDate).toDate(),
      );
      form.setFieldValue('classEnd', classEnd);
      form.setFieldValue('dayDiff', getDayDiff(dayjs(form.values.startDate), classEnd));
    } else {
      form.setFieldValue('classEnd', null);
      form.setFieldValue('dayDiff', '');
    }
  }, [form.values.duration, form.values.startTime, form.values.startDate]);

  return (
    <form id={formId} onSubmit={form.onSubmit(handleUpdate)}>
      <Stack spacing='xs'>
        <Group grow align='top'>
          <ClassTypeAutoComplete
            label='Class Type'
            placeholder='Select a class type'
            value={form.values.kiloClassType}
            error={form.errors.kiloClassType}
            onChange={handleClassTypeChange}
            required
            disabled
          />
          <div />
        </Group>

        <Paper withBorder p='sm' bg='gray.0'>
          <Stack spacing='sm'>
            {!form.values.classCoachClassCoachRoles.length && (
              <Text size='sm' color='dimmed'>
                No Coach
              </Text>
            )}
            {form.values.classCoachClassCoachRoles.map((ccccr, i) => (
              <Group
                key={ccccr.kiloClassCoachKiloClassCoachRoleId}
                position='apart'
                align='flex-start'
                noWrap
              >
                <StaffAutoComplete
                  label='Coach'
                  placeholder='Select a coach'
                  {...form.getInputProps(`classCoachClassCoachRoles.${i}.coach`)}
                  onChange={value => handleCoachChange(value, i)}
                  required
                  disinclude={form.values.classCoachClassCoachRoles.map(c => c.coach.staffId)}
                  isCoach
                />
                <ClassCoachRoleAutoComplete
                  label='Role'
                  placeholder='Select a role'
                  {...form.getInputProps(`classCoachClassCoachRoles.${i}.classCoachRole`)}
                  onChange={value => handleClassCoachRoleChange(value, i)}
                  required
                />
                <ActionIcon
                  mt='xl'
                  size='lg'
                  color='red'
                  onClick={() => form.removeListItem('classCoachClassCoachRoles', i)}
                >
                  <IconTrash size={theme.fontSizes.lg} />
                </ActionIcon>
              </Group>
            ))}
            <ActionIcon variant='transparent' color='kilo-blue' onClick={handleAddCoach}>
              <IconPlus />
            </ActionIcon>
          </Stack>
        </Paper>

        <Group grow align='top'>
          <LocationAutoComplete
            label='Location'
            placeholder='Select a location'
            value={form.values.location}
            onChange={handleLocationChange}
            error={form.errors.location}
          />
          <NumberInput
            {...form.getInputProps('capacity')}
            label='Capacity'
            placeholder='Enter class capacity'
            required
            min={1}
          />
        </Group>

        <Divider my='xs' mb={0} mx='-xl' />

        <Group grow align='top'>
          <NumberInput
            {...form.getInputProps('duration')}
            label='Duration in minutes'
            placeholder='Enter duration'
            required
            min={15}
            max={60 * 24 * 7}
          />
          <div />
        </Group>

        <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
          <DateInput {...form.getInputProps('startDate')} label='Start Date' locale='us' required disabled />
          <Group spacing='xs' grow>
            <TimeInput label='From' {...form.getInputProps('startTime')} required />
            <TimeInput
              label={
                <Group position='apart'>
                  <div>To</div>
                  <Text color='red'>{form.values.dayDiff}</Text>
                </Group>
              }
              value={form.values.classEnd ? form.values.classEnd.format('HH:mm') : ''}
              disabled
            />
          </Group>
        </SimpleGrid>
        <Group pt='xs'>
          <Stack>
            <Group>
              <Checkbox checked label='Re-occurring Event' disabled />
            </Group>

            <Stack pl='md'>
              <Radio.Group
                label='Recurrence Type'
                data-test='recurrence-type'
                value={form.values.frequency}
                onChange={handleFrequencyChange}
              >
                <Group grow align='stretch' pt='xs'>
                  {(
                    [
                      { value: 'weekly', title: 'Weekly', subtitle: 'Every 7 days' },
                      { value: 'biweekly', title: 'Biweekly', subtitle: 'Every 14 days' },
                      { value: 'monthly', title: 'Monthly', subtitle: 'Same date each calendar month' },
                    ] as const
                  ).map(option => (
                    <Paper key={option.value} withBorder radius='md' p='md'>
                      <Radio
                        value={option.value}
                        data-test={`recurrence-${option.value}`}
                        labelPosition='left'
                        styles={{ body: { width: '100%', justifyContent: 'space-between' } }}
                        label={
                          <Stack spacing={2}>
                            <Text fw={600}>{option.title}</Text>
                            <Text size='sm' color='dimmed'>
                              {option.subtitle}
                            </Text>
                          </Stack>
                        }
                      />
                    </Paper>
                  ))}
                </Group>
              </Radio.Group>
              {form.values.frequency !== 'monthly' && (
                <Checkbox.Group label='Days of the week' {...form.getInputProps('days')}>
                  <Group spacing='lg'>
                    <Checkbox
                      data-test='sunday'
                      value='SU'
                      label='Sun'
                      disabled={dayjs(form.values.startDate).day() === 0}
                    />
                    <Checkbox
                      data-test='monday'
                      value='MO'
                      label='Mon'
                      disabled={dayjs(form.values.startDate).day() === 1}
                    />
                    <Checkbox
                      data-test='tuesday'
                      value='TU'
                      label='Tues'
                      disabled={dayjs(form.values.startDate).day() === 2}
                    />
                    <Checkbox
                      data-test='wednesday'
                      value='WE'
                      label='Wed'
                      disabled={dayjs(form.values.startDate).day() === 3}
                    />
                    <Checkbox
                      data-test='thursday'
                      value='TH'
                      label='Thu'
                      disabled={dayjs(form.values.startDate).day() === 4}
                    />
                    <Checkbox
                      data-test='friday'
                      value='FR'
                      label='Fri'
                      disabled={dayjs(form.values.startDate).day() === 5}
                    />
                    <Checkbox
                      data-test='saturday'
                      value='SA'
                      label='Sat'
                      disabled={dayjs(form.values.startDate).day() === 6}
                    />
                  </Group>
                </Checkbox.Group>
              )}
              <Group grow align='top'>
                <DateInput
                  {...form.getInputProps('endDate')}
                  label='End Date'
                  placeholder='Never'
                  data-test='endDate'
                  minDate={form.values.startDate}
                  clearable
                />
                <div />
              </Group>
            </Stack>
          </Stack>
        </Group>

        <Divider mt='lg' mb={0} mx='-xl' />

        <ExpandableSection
          title='Additional Settings'
          opened={settingsOpened}
          handleToggle={handleSettingsToggle}
        >
          <Stack spacing='sm' px='md'>
            <Input.Wrapper label='Waitlist capacity'>
              <SimpleGrid cols={2}>
                <NumberInput min={0} {...form.getInputProps('waitlistLimit')} required />
              </SimpleGrid>
            </Input.Wrapper>
            <Input.Wrapper label='The amount of time before a class when reservations will be accepted'>
              <SimpleGrid cols={2}>
                <NumberInput
                  min={0}
                  {...form.getInputProps(
                    'reservationAndCancellationSettings.reservationWindowOpenIntervalCount',
                  )}
                  required
                />
                <Select
                  data={timeUnitOptions}
                  {...form.getInputProps('reservationAndCancellationSettings.reservationWindowOpenInterval')}
                />
              </SimpleGrid>
            </Input.Wrapper>
            <Input.Wrapper label='The amount of time before a class when reservations will no longer be accepted'>
              <SimpleGrid cols={2}>
                <NumberInput
                  min={0}
                  {...form.getInputProps(
                    'reservationAndCancellationSettings.reservationWindowCloseIntervalCount',
                  )}
                  required
                />
                <Select
                  data={timeUnitOptions}
                  {...form.getInputProps(
                    'reservationAndCancellationSettings.reservationWindowCloseInterval',
                  )}
                />
              </SimpleGrid>
            </Input.Wrapper>
            <Input.Wrapper>
              <SimpleGrid cols={1}>
                <Switch
                  my='xs'
                  label='Allow late cancellation'
                  checked={form.values.reservationAndCancellationSettings.allowLateCancellation}
                  {...form.getInputProps('reservationAndCancellationSettings.allowLateCancellation')}
                />
              </SimpleGrid>
            </Input.Wrapper>
            <Input.Wrapper
              label={
                form.values.reservationAndCancellationSettings.allowLateCancellation
                  ? 'Time before the start of a class when a cancellation is considered late'
                  : 'Time before the start of a class when cancellations are no longer allowed'
              }
            >
              <SimpleGrid cols={2}>
                <NumberInput
                  min={0}
                  max={setMaxLateCancellationIntervalCount(
                    form.values.reservationAndCancellationSettings.cancellationInterval,
                  )}
                  {...form.getInputProps('reservationAndCancellationSettings.cancellationIntervalCount')}
                  required
                />
                <Select
                  data={timeUnitOptions}
                  {...form.getInputProps('reservationAndCancellationSettings.cancellationInterval')}
                />
              </SimpleGrid>
            </Input.Wrapper>
            {form.values.reservationAndCancellationSettings.allowLateCancellation && (
              <>
                <Group noWrap>
                  <Switch
                    checked={form.values.useLateCancellationFee}
                    {...form.getInputProps('useLateCancellationFee')}
                  />
                  <Text size='sm'>Auto-Charge Fee for late cancellations</Text>
                  {form.values.useLateCancellationFee && (
                    <MoneyField
                      country={country}
                      min={0}
                      {...form.getInputProps('reservationAndCancellationSettings.cancellationFee')}
                    />
                  )}
                </Group>

                <Input.Wrapper>
                  <SimpleGrid cols={1}>
                    <Switch
                      my='xs'
                      label='Deduction of credit for late cancellation'
                      checked={
                        form.values?.reservationAndCancellationSettings?.cancellationCreditDeduction || false
                      }
                      {...form.getInputProps(
                        'reservationAndCancellationSettings.cancellationCreditDeduction',
                      )}
                    />
                  </SimpleGrid>
                </Input.Wrapper>
              </>
            )}
            <Divider />
            <Group noWrap>
              <Switch checked={form.values.useNoShowFee} {...form.getInputProps('useNoShowFee')} />
              <Text size='sm'>Auto-Charge Fee for No Shows</Text>
              {form.values.useNoShowFee && (
                <MoneyField
                  country={country}
                  min={0}
                  {...form.getInputProps('reservationAndCancellationSettings.noShowFee')}
                />
              )}
            </Group>
            <Input.Wrapper>
              <SimpleGrid cols={1}>
                <Switch
                  my='xs'
                  label='Deduction of credit for No Shows'
                  checked={form.values?.reservationAndCancellationSettings?.noShowCreditDeduction || false}
                  {...form.getInputProps('reservationAndCancellationSettings.noShowCreditDeduction')}
                />
              </SimpleGrid>
            </Input.Wrapper>
          </Stack>
        </ExpandableSection>
      </Stack>
    </form>
  );
};

export default DetailsEditAllFutureClassesForm;
